import React from "react";
import { withRouter } from "react-router-dom";
import axios from "axios";

import "./Dashboard.css";
import { getAdminMenus, isSuperAdmin } from "../utils/accessControl";

const formatCurrency = value => new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
}).format(Number(value) || 0);

const compactCurrency = value => {
  const amount = Number(value) || 0;
  if (amount >= 1000000000) return `Rp ${(amount / 1000000000).toFixed(1)} M`;
  if (amount >= 1000000) return `Rp ${(amount / 1000000).toFixed(1)} Jt`;
  if (amount >= 1000) return `Rp ${(amount / 1000).toFixed(0)} Rb`;
  return `Rp ${amount.toLocaleString("id-ID")}`;
};

const formatDate = value => {
  if (!value) return "-";
  const normalized = /^\d{2}-\d{2}-\d{4}/.test(value)
    ? value.replace(/^(\d{2})-(\d{2})-(\d{4})/, "$3-$2-$1")
    : value;
  const date = new Date(normalized);

  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "short", year: "numeric" }).format(date);
};

const getStatusTone = (status, name = "") => {
  const normalizedName = name.toLowerCase();
  if (String(status) === "5" || /selesai|lunas|success|settlement/.test(normalizedName)) return "success";
  if (["98", "99"].includes(String(status)) || /batal|refund|gagal|expire/.test(normalizedName)) return "danger";
  if (/menunggu|pending|proses/.test(normalizedName)) return "warning";
  return "info";
};

const getSellerDetails = transaction => {
  const details = transaction?.seller_details;
  if (details && typeof details === "object") return details;
  if (typeof details !== "string") return {};

  try {
    return JSON.parse(details) || {};
  } catch (error) {
    return {};
  }
};

const getMerchantName = transaction => getSellerDetails(transaction).name || "Merchant tidak diketahui";

const getInitialPeriod = () => {
  if (isSuperAdmin()) return 30;
  const routes = getAdminMenus().map(menu => menu.route || "");
  const hasBursaKopi = routes.some(route => route.indexOf("/bursa-kopi/") === 0);
  const ecommerceRoutes = ["/merchant", "/merchant/performance", "/transaction", "/reportinvoice", "/reportinvoicefull", "/refund", "/checkpayout", "/approvepayout", "/reportpayout"];
  const hasEcommerce = routes.some(route => ecommerceRoutes.includes(route));

  return hasBursaKopi && !hasEcommerce ? "all" : 30;
};

const applicationMeta = {
  "1toko": { icon: "fas fa-shopping-bag", label: "1Toko", tone: "blue" },
  bursakopi: { icon: "fas fa-mug-hot", label: "Bursa Kopi", tone: "coffee" },
  cekgudang: { icon: "fas fa-project-diagram", label: "SCM 360", tone: "teal" },
  kliksekolah_ypl: { icon: "fas fa-school", label: "Kliksekolah YPL", tone: "orange" },
  kliksekolah_general: { icon: "fas fa-university", label: "Kliksekolah General", tone: "purple" },
};

const platformMeta = [
  { parent: "ecommerce", icon: "fas fa-shopping-bag", label: "1Toko" },
  { parent: "bursa-kopi", icon: "fas fa-mug-hot", label: "Bursa Kopi" },
  { parent: "kliksekolah", icon: "fas fa-school", label: "Kliksekolah" },
  { parent: "cekgudang", icon: "fas fa-project-diagram", label: "SCM 360" },
];

const getApplicationMeta = application => applicationMeta[application.key] || {
  icon: "fas fa-th-large",
  label: application.name || "Aplikasi",
  tone: "slate",
};

class Dashboard extends React.Component {
  state = {
    data: null,
    period: getInitialPeriod(),
    loading: true,
    error: "",
  };

  componentDidMount() {
    this.fetchDashboard(this.state.period);
  }

  fetchDashboard = async period => {
    this.setState({ loading: true, error: "", period });

    try {
      const response = await axios.get(
        global.variable.LINK_API + `report/dashboard?period=${period}`,
        { ...global.variable.HEADERS_BEARER, cancelKey: "dashboard-summary" }
      );
      this.setState({ data: response.data });
    } catch (error) {
      this.setState({
        error: error?.response?.data?.message || "Ringkasan dashboard belum dapat dimuat.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  openTransaction = (invoiceId, detailPath = "/transactiondetail") => {
    this.props.history.push(detailPath + "/" + encodeURIComponent(invoiceId));
  };

  renderChange = value => {
    if (value === null || typeof value === "undefined") {
      return <span className="dashboard-kpi__rate">Semua waktu</span>;
    }
    const amount = Number(value) || 0;
    const positive = amount >= 0;

    return (
      <span className={`dashboard-change dashboard-change--${positive ? "positive" : "negative"}`}>
        <i className={`fas fa-arrow-${positive ? "up" : "down"}`} />
        {Math.abs(amount).toLocaleString("id-ID")}%
      </span>
    );
  };

  render() {
    const { data, period, loading, error } = this.state;
    const summary = data?.summary || {};
    const sla = data?.sla || {};
    const trend = (data?.trend || []).slice(-14);
    const maxTrendAmount = Math.max(...trend.map(item => Number(item.amount) || 0), 1);
    const statuses = data?.statuses || [];
    const applications = data?.applications || [];
    const accessParents = data?.access_parents || [];
    const insightSource = data?.insight_source || (accessParents.includes("ecommerce") ? {
      key: "1toko",
      name: "1Toko",
      transaction_path: "/transaction",
      detail_path: "/transactiondetail",
    } : accessParents.includes("bursa-kopi") ? {
      key: "bursakopi",
      name: "Bursa Kopi",
      transaction_path: "/bursa-kopi/transaction",
      detail_path: "/bursa-kopi/transactiondetail",
    } : null);
    const accessiblePlatforms = platformMeta.filter(platform => accessParents.includes(platform.parent));
    const showCommerceInsights = Boolean(insightSource);
    const insightName = insightSource?.name || "platform";
    const showSlaInsights = accessParents.includes("intelligence");
    const activeApplicationCount = applications.filter(application => application.available !== false).length;
    const topMerchants = data?.top_merchants || [];
    const recentTransactions = data?.recent_transactions || [];
    const today = new Intl.DateTimeFormat("id-ID", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date());

    if (loading && !data) {
      return (
        <div className="dashboard-state">
          <span className="dashboard-state__loader" />
          <h2>Menyiapkan dashboard</h2>
          <p>Mengumpulkan ringkasan transaksi terbaru.</p>
        </div>
      );
    }

    if (error && !data) {
      return (
        <div className="dashboard-state dashboard-state--error">
          <span><i className="fas fa-exclamation-triangle" /></span>
          <h2>Dashboard tidak dapat dimuat</h2>
          <p>{error}</p>
          <button type="button" onClick={() => this.fetchDashboard(period)}>Coba lagi</button>
        </div>
      );
    }

    return (
      <section className="dashboard-page">
        <div className="dashboard-hero">
          <div className="dashboard-hero__content">
            <span className="dashboard-hero__eyebrow"><i className="fas fa-satellite-dish" /> UNIFIED MANAGEMENT CENTER</span>
            <h1>Selamat datang di SPIN Management Portal</h1>
            <p>Pantau performa transaksi dan aktivitas platform dari satu pusat kendali.</p>
            <div className="dashboard-hero__platforms">
              {accessiblePlatforms.map(platform => (
                <span key={platform.parent}><i className={platform.icon} /> {platform.label}</span>
              ))}
            </div>
          </div>
          <div className="dashboard-hero__control">
            <small>{today}</small>
            <div className="dashboard-hero__actions">
              <label>
                <i className="far fa-calendar-alt" />
                <select value={period} onChange={event => this.fetchDashboard(event.target.value === "all" ? "all" : Number(event.target.value))}>
                  <option value={7}>7 hari terakhir</option>
                  <option value={30}>30 hari terakhir</option>
                  <option value={90}>90 hari terakhir</option>
                  {(period === "all" || insightSource?.key === "bursakopi") && <option value="all">Semua waktu</option>}
                </select>
              </label>
              <button type="button" onClick={() => this.fetchDashboard(period)} disabled={loading} aria-label="Perbarui dashboard">
                <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`} />
              </button>
            </div>
          </div>
        </div>

        {error && <div className="dashboard-inline-alert"><i className="fas fa-info-circle" /> {error}</div>}

        {showCommerceInsights && <div className="dashboard-kpis">
          <article className="dashboard-kpi dashboard-kpi--transactions">
            <div className="dashboard-kpi__top">
              <span className="dashboard-kpi__icon dashboard-kpi__icon--purple"><i className="fas fa-receipt" /></span>
              {this.renderChange(summary.transaction_change)}
            </div>
            <span>Total transaksi</span>
            <strong>{Number(summary.total_transactions || 0).toLocaleString("id-ID")}</strong>
            <small>Dibanding periode sebelumnya</small>
          </article>
          <article className="dashboard-kpi dashboard-kpi--amount">
            <div className="dashboard-kpi__top">
              <span className="dashboard-kpi__icon dashboard-kpi__icon--blue"><i className="fas fa-wallet" /></span>
              {this.renderChange(summary.amount_change)}
            </div>
            <span>Nilai transaksi</span>
            <strong>{compactCurrency(summary.total_amount)}</strong>
            <small>{formatCurrency(summary.total_amount)}</small>
          </article>
          <article className="dashboard-kpi dashboard-kpi--success">
            <div className="dashboard-kpi__top">
              <span className="dashboard-kpi__icon dashboard-kpi__icon--green"><i className="fas fa-check-circle" /></span>
              <span className="dashboard-kpi__rate">{Number(summary.success_rate || 0).toLocaleString("id-ID")}%</span>
            </div>
            <span>Transaksi berhasil</span>
            <strong>{Number(summary.success_transactions || 0).toLocaleString("id-ID")}</strong>
            <small>Success rate periode berjalan</small>
          </article>
          <article className="dashboard-kpi dashboard-kpi--refund">
            <div className="dashboard-kpi__top">
              <span className="dashboard-kpi__icon dashboard-kpi__icon--red"><i className="fas fa-undo-alt" /></span>
            </div>
            <span>Refund / dibatalkan</span>
            <strong>{Number(summary.refund_transactions || 0).toLocaleString("id-ID")}</strong>
            <small>Perlu perhatian operasional</small>
          </article>
        </div>}

        {showSlaInsights && <article className="dashboard-sla-overview">
          <div className="dashboard-sla-overview__identity">
            <span><i className="fas fa-stopwatch" /></span>
            <div><small>OPERATIONAL SLA</small><h2>SLA &amp; Aging lintas sumber</h2><p>Monitoring read-only untuk invoice dan payout yang masih terbuka.</p></div>
          </div>
          <div className="dashboard-sla-overview__metrics">
            <div><span>Compliance</span><strong>{Number(sla.compliance_rate || 0).toLocaleString("id-ID")}%</strong><small>{sla.available_sources || 0}/{sla.total_sources || 0} sumber aktif</small></div>
            <div className="dashboard-sla-overview__metric dashboard-sla-overview__metric--danger"><span>Overdue</span><strong>{Number(sla.overdue || 0).toLocaleString("id-ID")}</strong><small>Melewati target</small></div>
            <div className="dashboard-sla-overview__metric dashboard-sla-overview__metric--warning"><span>Mendekati SLA</span><strong>{Number(sla.due_soon || 0).toLocaleString("id-ID")}</strong><small>Pemakaian 70–100%</small></div>
            <div><span>Rata-rata aging</span><strong>{Number(sla.average_age_hours || 0).toLocaleString("id-ID")} jam</strong><small>{Number(sla.total_open || 0).toLocaleString("id-ID")} item terbuka</small></div>
          </div>
          <button type="button" onClick={() => this.props.history.push("/sla-aging")}>Buka SLA &amp; Aging <i className="fas fa-arrow-right" /></button>
        </article>}

        <article className="dashboard-panel dashboard-applications">
          <div className="dashboard-panel__header dashboard-applications__header">
            <div><span>PORTFOLIO APLIKASI</span><h2>Ringkasan performa lintas aplikasi</h2><p>Perbandingan aktivitas berdasarkan cakupan data masing-masing aplikasi.</p></div>
            <div className="dashboard-applications__count">
              <span><i className="fas fa-layer-group" /> {activeApplicationCount}/{applications.length}</span>
              <small>Sumber data aktif</small>
            </div>
          </div>
          <div className="dashboard-applications__grid">
            {applications.length ? applications.map((application, applicationIndex) => {
              const meta = getApplicationMeta(application);
              const available = application.available !== false;
              return (
                <section className={`dashboard-application dashboard-application--${meta.tone} ${available ? "" : "dashboard-application--unavailable"}`} key={application.key}>
                  <div className="dashboard-application__top">
                    <div className="dashboard-application__source">
                      <span className="dashboard-application__icon"><i className={meta.icon} /></span>
                      <small>SUMBER {String(applicationIndex + 1).padStart(2, "0")}</small>
                    </div>
                    <span className={`dashboard-application__status dashboard-application__status--${available ? "online" : "offline"}`}><i />{available ? "Data aktif" : "Tidak tersedia"}</span>
                  </div>
                  <div className="dashboard-application__title">
                    <div><h3>{application.name || meta.label}</h3>{application.scope_label && <span>{application.scope_label}</span>}</div>
                    <p>{application.description || "Ringkasan transaksi aplikasi"}</p>
                  </div>
                  {available ? <>
                    <div className="dashboard-application__amount">
                      <span>Nilai transaksi</span>
                      <strong>{compactCurrency(application.total_amount)}</strong>
                    </div>
                    <div className="dashboard-application__metrics">
                      <div><span>Transaksi</span><strong>{Number(application.total_transactions || 0).toLocaleString("id-ID")}</strong></div>
                      <div><span>Berhasil</span><strong>{Number(application.success_rate || 0).toLocaleString("id-ID")}%</strong></div>
                      <div><span>Rata-rata</span><strong>{compactCurrency(application.average_amount)}</strong></div>
                    </div>
                    {application.scope === "all_time" && <small className="dashboard-application__cache-note"><i className="fas fa-bolt" /> Ringkasan cepat diperbarui maksimal setiap 10 menit</small>}
                  </> : <div className="dashboard-application__notice"><i className="fas fa-exclamation-circle" /><span>{application.status_note || "Sumber data belum dapat dihubungi."}</span></div>}
                  <footer className="dashboard-application__footer">
                    <span><i className="far fa-clock" /> Aktivitas terakhir</span>
                    <strong>{formatDate(application.last_activity)}</strong>
                  </footer>
                </section>
              );
            }) : <div className="dashboard-applications__empty"><i className="fas fa-user-shield" /><div><strong>Belum ada akses platform</strong><span>Pilih parent platform pada menu User Access untuk menampilkan ringkasannya.</span></div></div>}
          </div>
        </article>

        {showCommerceInsights && <>
        <div className="dashboard-main-grid">
          <article className="dashboard-panel dashboard-panel--trend">
            <div className="dashboard-panel__header">
              <div><span>PERFORMA {insightName.toUpperCase()}</span><h2>Tren nilai transaksi</h2><p>Aktivitas 14 hari terakhir dari periode terpilih.</p></div>
              <div className="dashboard-panel__tools"><span className="dashboard-panel__legend"><i /> Nilai transaksi</span></div>
            </div>
            <div className="dashboard-chart">
              {trend.length ? trend.map((item, index) => {
                const height = Math.max(5, ((Number(item.amount) || 0) / maxTrendAmount) * 100);
                return (
                  <div className="dashboard-chart__column" key={item.date}>
                    <div className="dashboard-chart__tooltip">
                      <strong>{formatCurrency(item.amount)}</strong><small>{item.total} transaksi</small>
                    </div>
                    <div className="dashboard-chart__track"><span style={{ height: `${height}%` }} /></div>
                    <small>{index % 2 === 0 || trend.length <= 7 ? formatDate(item.date).split(" ").slice(0, 2).join(" ") : ""}</small>
                  </div>
                );
              }) : <div className="dashboard-chart__empty">Belum ada transaksi pada periode ini.</div>}
            </div>
          </article>

          <article className="dashboard-panel">
            <div className="dashboard-panel__header">
              <div><span>DISTRIBUSI</span><h2>Status transaksi</h2><p>Komposisi status periode berjalan.</p></div>
            </div>
            <div className="dashboard-status-list">
              {statuses.length ? statuses.slice(0, 5).map(status => {
                const tone = getStatusTone(status.status, status.name);
                return (
                  <div className="dashboard-status-item" key={`${status.status}-${status.name}`}>
                    <div><span className={`dashboard-status-item__dot dashboard-status-item__dot--${tone}`} /><strong>{status.name}</strong><small>{status.total} transaksi</small></div>
                    <span>{Number(status.percentage).toLocaleString("id-ID")}%</span>
                    <div className="dashboard-status-item__bar"><i className={`dashboard-status-item__bar--${tone}`} style={{ width: `${Math.min(Number(status.percentage), 100)}%` }} /></div>
                  </div>
                );
              }) : <p className="dashboard-panel__empty">Belum ada distribusi status.</p>}
            </div>
          </article>
        </div>

        <div className="dashboard-secondary-grid">
          <article className="dashboard-panel">
            <div className="dashboard-panel__header">
              <div><span>MERCHANT {insightName.toUpperCase()}</span><h2>Merchant teratas</h2><p>Berdasarkan nilai transaksi.</p></div>
            </div>
            <div className="dashboard-merchant-list">
              {topMerchants.length ? topMerchants.map((merchant, index) => (
                <div className="dashboard-merchant" key={`${merchant.name}-${index}`}>
                  <span className="dashboard-merchant__rank">{index + 1}</span>
                  <span className="dashboard-merchant__avatar">{merchant.name.charAt(0).toUpperCase()}</span>
                  <div><strong>{merchant.name}</strong><small>{merchant.total} transaksi</small></div>
                  <strong>{compactCurrency(merchant.amount)}</strong>
                </div>
              )) : <p className="dashboard-panel__empty">Belum ada data merchant.</p>}
            </div>
          </article>

          <article className="dashboard-panel dashboard-panel--recent">
            <div className="dashboard-panel__header">
              <div><span>TERBARU · {insightName.toUpperCase()}</span><h2>Transaksi terbaru</h2><p>Invoice yang baru masuk ke sistem.</p></div>
              <button type="button" onClick={() => this.props.history.push(insightSource.transaction_path)}>Lihat semua <i className="fas fa-arrow-right" /></button>
            </div>
            <div className="dashboard-table-wrap">
              <table className="dashboard-table">
                <thead><tr><th>Invoice</th><th>Merchant</th><th>Status</th><th>Nominal</th><th>Tanggal</th></tr></thead>
                <tbody>
                  {recentTransactions.map(transaction => {
                    const tone = getStatusTone(transaction.status, transaction.status_name);
                    return (
                      <tr key={transaction.invoice_id} onClick={() => this.openTransaction(transaction.invoice_id, insightSource.detail_path)}>
                        <td><strong>{transaction.invoice_id}</strong><small>{transaction.order_id}</small></td>
                        <td>{getMerchantName(transaction)}</td>
                        <td><span className={`dashboard-table__status dashboard-table__status--${tone}`}><i />{transaction.status_name || "Status lainnya"}</span></td>
                        <td><strong>{formatCurrency(transaction.gross_amount)}</strong></td>
                        <td>{formatDate(transaction.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!recentTransactions.length && <p className="dashboard-panel__empty">Belum ada transaksi terbaru.</p>}
            </div>
          </article>
        </div>
        </>}
      </section>
    );
  }
}

export default withRouter(Dashboard);
